import {
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
  type QueryKey,
} from '@tanstack/react-query';
import service from '../service';
import type { Dashboard } from '../types/dashboard';

type DashboardsResponse = { data: Dashboard[]; meta: { total: number } };

type DefaultError = unknown;

export const dashboardQueryKeys = {
  all: ['dashboards'] as const,
  lists: () => [...dashboardQueryKeys.all, 'list'] as const,
  list: (params: Record<string, string>) =>
    [...dashboardQueryKeys.lists(), params] as const,
  details: () => [...dashboardQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...dashboardQueryKeys.details(), id] as const,
};

type UseDashboardsParams = {
  searchParams?: Record<string, string>;
  enabled?: boolean;
};

export function useDashboards<
  TData = DashboardsResponse,
  TError = DefaultError,
>(
  { searchParams = {}, enabled = true }: UseDashboardsParams = {},
  options?: Omit<
    UseQueryOptions<DashboardsResponse, TError, TData, QueryKey>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
): UseQueryResult<TData, TError> {
  const queryKey: QueryKey = dashboardQueryKeys.list(searchParams);

  return useQuery<DashboardsResponse, TError, TData>({
    queryKey,
    enabled,
    queryFn: async () => service.getDashboards(searchParams),
    ...(options as any),
  });
}
